// `usePasswordStrength` — wraps zxcvbn behind a lazy import.
//
// DESIGN.md §1.1 + O1: zxcvbn computes a 0–4 score; we enable the submit
// button at score >= 2 (the server still enforces the top-100k denylist).
// We lazy-load `zxcvbn` on first use to keep the initial bundle slim.

import { useEffect, useRef, useState } from 'react';

export type PasswordScore = 0 | 1 | 2 | 3 | 4;

export interface PasswordStrength {
  /** zxcvbn score (0–4). `null` while the analyzer is loading. */
  score: PasswordScore | null;
  /** True once the analyzer has loaded (so callers can show a meter). */
  ready: boolean;
}

type ZxcvbnFn = (password: string, userInputs?: string[]) => { score: number };

let zxcvbnPromise: Promise<ZxcvbnFn> | undefined;

/**
 * Lazy-load zxcvbn once and cache the resolved fn. Sub-modules of zxcvbn
 * still ship in the same chunk; the `import()` cost is the load itself.
 */
function loadZxcvbn(): Promise<ZxcvbnFn> {
  if (!zxcvbnPromise) {
    zxcvbnPromise = import('zxcvbn').then((mod) => {
      const fn = (mod.default ?? (mod as unknown as ZxcvbnFn)) as ZxcvbnFn;
      return fn;
    });
  }
  return zxcvbnPromise;
}

/**
 * Compute a debounced strength score for the given password. Returns
 * `score: null` until the analyzer has loaded; thereafter mirrors zxcvbn's
 * 0–4 score.
 */
export function usePasswordStrength(password: string, userInputs: string[] = []): PasswordStrength {
  const [ready, setReady] = useState<boolean>(false);
  const [score, setScore] = useState<PasswordScore | null>(null);
  const analyzerRef = useRef<ZxcvbnFn | null>(null);

  // Boot the analyzer on first render that has a non-empty password.
  useEffect(() => {
    if (analyzerRef.current || !password) return;
    let cancelled = false;
    void loadZxcvbn().then((fn) => {
      if (cancelled) return;
      analyzerRef.current = fn;
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [password]);

  useEffect(() => {
    if (!ready) return;
    if (!password) {
      setScore(null);
      return;
    }
    const fn = analyzerRef.current;
    if (!fn) return;
    // Tiny debounce — we don't want every keystroke to trigger zxcvbn's
    // dictionary scan during fast typing.
    const handle = setTimeout(() => {
      const result = fn(password, userInputs);
      const next = clampScore(result.score);
      setScore(next);
    }, 120);
    return () => {
      clearTimeout(handle);
    };
  }, [password, userInputs, ready]);

  return { score, ready };
}

function clampScore(value: number): PasswordScore {
  if (value <= 0) return 0;
  if (value >= 4) return 4;
  return Math.round(value) as PasswordScore;
}

/** The minimum zxcvbn score that allows form submission (DESIGN O1). */
export const MIN_PASSWORD_SCORE: PasswordScore = 2;

/**
 * Client-side weak-password gate shared by SignUp + ResetPassword. A `null`
 * score (analyzer not loaded yet) is treated as "not too weak" — the server
 * is the gate in that window (DESIGN O1).
 */
export function isPasswordTooWeak(score: PasswordScore | null): boolean {
  return score !== null && score < MIN_PASSWORD_SCORE;
}
